import { AxiosError } from 'axios';
import React, { useRef, useState, useEffect } from 'react';
import { Alert, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formPost } from 'src/utils/clientRequests';
import {
  validateEmailPattern,
  validatePostalCodePattern,
  validateRequired,
  validateRequiredEmail,
  validateRequiredFirstName,
  validateRequiredCity,
  validateRequiredProvince,
  validateRequiredLastName,
  validateRequiredPostalCode,
  validateRequiredPetName,
  validateAddressUnitPattern,
} from 'src/utils/formHelpers';
import RichText from '../rich_text/RichText';

interface FormValues {
  date: string;
  firstName: string;
  lastName: string;
  petName: string | 'Kitten';
  petBirthday: string;
  petType: 'CAT';
  email: string;
  address1: string,
  address2: string,
  city: string,
  province: string,
  postalCode: string,
  flavour: string,
  locale: {
    keyName: Languages;
  };
  agreed: boolean;
}

interface FreeSampleFormProps {
  getFieldDataByName: Function;
  langcode: Languages;
  petType: 'CAT';
  petName: 'Kitten';
  webformId: string;
  formOptIn?: string;
}

const FreeSampleForm = ({
  getFieldDataByName,
  langcode,
  petType,
  petName,
  webformId,
  formOptIn,
}: FreeSampleFormProps) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      date: new Date().toISOString().slice(0, 10),
      firstName: '',
      lastName: '',
      petName: '',
      petBirthday: new Date().toISOString().slice(0, 10),
      petType,
      email: '',
      address1: '',
      address2: '',
      city: '',
      province: '',
      postalCode: '',
      flavour: '',
      locale: {
        keyName: 'en',
      },
      agreed: false,
    },
  });

  const [formStatus, setFormStatus] = useState({ open: true, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [apiError, setApiError] = useState('');
  const apiRef = useRef<HTMLDivElement>(null);

  // Pet birthdate max validation.
  const maxToday = new Date();
  const plusYear = new Date(maxToday.setFullYear(maxToday.getFullYear() + 1));
  const maxBirthDate: string = plusYear.toISOString().split('T')[0];

  const onSubmit: SubmitHandler<FormValues> = async formData => {
    setSubmitting(true);
    const formattedData = {
      webform_id: webformId,
      date: formData.date,
      first_name: formData.firstName,
      last_name: formData.lastName,
      pet_name: formData.petName === '' ? petName : formData.petName,
      pet_birthday: formData.petBirthday,
      pet_type: formData.petType,
      email_address: formData.email,
      address_1: formData.address1,
      address_2: formData.address2,
      city: formData.city,
      province: formData.province,
      postal_code: formData.postalCode,
      flavour: formData.flavour,
      language: langcode || 'en',
      agree_box: formData.agreed,
    };

    const freeSampleResponse = await formPost({ data: formattedData });

    if (!freeSampleResponse) {
      setApiError('Something went wrong');
      setSubmitting(false);
      setTimeout(() => {
        apiRef.current?.focus();
      }, 500);
      return;
    }

    if (freeSampleResponse instanceof AxiosError) {
      setApiError(freeSampleResponse.response?.data?.error?.email_address || freeSampleResponse.response?.data?.error?.message || 'Something went wrong');
      setSubmitting(false);
      setTimeout(() => {
        apiRef.current?.focus();
      }, 500);
      return;
    }

    setSubmitted(true);
    setSubmitting(false);
  };

  if (submitting) {
    return (
      <Row className="justify-content-center py-5">
        <Col className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{`${t('Submitting')}...`}</span>
          </Spinner>
          <span className="ms-3">{`${t('Please wait')}...`}</span>
        </Col>
      </Row>
    );
  }
  if (submitted) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} className="text-center">
            <div className="mb-3" aria-hidden="true" style={{ fontSize: '3rem' }}>&#10003;</div>
            <RichText body={getFieldDataByName('success_message')?.text} />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-md-center gx-3 gy-3 ">
        <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          {apiError && (
            <Alert variant="error" role="alert" tabIndex={0} ref={apiRef}>
              <RichText body={apiError} />
            </Alert>
          )}
          <Row className="gx-3 gy-3">
            <Col xs={12} md={6}>
              <Form.Group controlId="firstName" className="required">
                <Form.Label>{getFieldDataByName('first_name').title}</Form.Label>
                <Form.Control
                  {...register('firstName', {
                    required: validateRequiredFirstName(t),
                  })}
                  type="text"
                  maxLength={60}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.firstName && errors.firstName.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="lastName" className="required">
                <Form.Label>{getFieldDataByName('last_name').title}</Form.Label>
                <Form.Control
                  {...register('lastName', {
                    required: validateRequiredLastName(t),
                  })}
                  type="text"
                  maxLength={60}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.lastName && errors.lastName.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12} md={6}>
              <Form.Group controlId="petName" className="required">
                <Form.Label>{getFieldDataByName('pet_name').title}</Form.Label>
                <Form.Control
                  {...register('petName', {
                    required: validateRequiredPetName(t)
                  })}
                  type="text"
                  maxLength={60}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.petName && errors.petName.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="petBirthday" className="required">
                <Form.Label>{getFieldDataByName('pet_birthday').title}</Form.Label>
                <Form.Control
                  {...register('petBirthday', {
                    required: validateRequired('Pet Birthday', t),
                    validate: value => {
                      // Extract the year from the date string (format: YYYY-MM-DD)
                      const yearStr = value.split('-')[0];
                      const year = parseInt(yearStr, 10);
                      const currentYear = new Date().getFullYear();

                      // Check if the year is within a reasonable range
                      if (year < 2000 || year > currentYear) {
                        return t('Please enter a year between 2000 and the current year');
                      }

                      return true;
                    },
                  })}
                  type="date"
                  max={maxBirthDate}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.petBirthday && errors.petBirthday.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12} md={6}>
              <Form.Group controlId="email" className="required">
                <Form.Label>{getFieldDataByName('email_address').title}</Form.Label>
                <Form.Control
                  {...register('email', {
                    required: validateRequiredEmail(t),
                    pattern: validateEmailPattern(t),
                  })}
                  type="email"
                  placeholder="example@gmail.com"
                  maxLength={128}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.email && errors.email.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="flavour" className="required">
                <Form.Label>{getFieldDataByName('flavour').title}</Form.Label>
                <Form.Select {...register('flavour')}>
                  <option value={undefined} />
                  <option value={getFieldDataByName('flavour')?.options[0].value}>
                    {getFieldDataByName('flavour')?.options[0].label}
                  </option>
                  <option value={getFieldDataByName('flavour')?.options[1].value}>
                    {getFieldDataByName('flavour')?.options[1].label}
                  </option>
                </Form.Select>
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.flavour && errors.flavour.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12} md={12}>
              <Form.Group controlId="address1" className="required">
                <Form.Label>{getFieldDataByName('address_1').title}</Form.Label>
                <Form.Control
                  {...register('address1')}
                  type="text"
                  maxLength={128}
                  aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.address1 && errors.address1.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12} md={6}>
              <Form.Group controlId="address2">
                <Form.Label>{getFieldDataByName('address_2').title}</Form.Label>
                <Form.Control
                    {...register('address2', {
                      pattern: validateAddressUnitPattern(t)
                    })}
                    type="text"
                    maxLength={200}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.address2 && errors.address2.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="city" className="required">
                <Form.Label>{getFieldDataByName('city').title}</Form.Label>
                <Form.Control
                    {...register('city', {
                      required: validateRequiredCity(t)
                    })}
                    type="text"
                    maxLength={60}
                    aria-required="true"
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.city && errors.city.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12} md={6}>
              <Form.Group controlId="province" className="required">
                <Form.Label>{getFieldDataByName('province').title}</Form.Label>
                <Form.Select {...register('province', {
                  required: validateRequiredProvince(t)
                })}>
                  <option value={undefined} />
                  {getFieldDataByName('province')?.options?.slice(0, 12).map((option, index) => (
                      <option key={option.value || index} value={option.value}>
                        {option.label}
                      </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.province && errors.province.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="postalCode" className="required">
                <Form.Label>{getFieldDataByName('postal_code').title}</Form.Label>
                <Form.Control
                    {...register('postalCode', {
                      required: validateRequiredPostalCode(t),
                      pattern: validatePostalCodePattern(t),
                    })}
                    type="postalCode"
                    maxLength={128}
                    aria-required="true"
                    isInvalid={!!errors?.postalCode}
                    aria-invalid={!!errors?.postalCode}
                    required
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.postalCode && errors.postalCode.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {formOptIn && (
            <Row className="gx-3 gy-3 mt-1">
              <Col xs={12}>
                <RichText body={formOptIn} />
              </Col>
            </Row>
          )}
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12}>
              <Form.Group controlId="agreed">
                <Form.Check typography-h2 type="checkbox">
                  <Form.Check.Input {...register('agreed')} type="checkbox" />
                  <Form.Check.Label aria-hidden="true">
                    {getFieldDataByName('disclaimer').text && (
                      <RichText body={getFieldDataByName('disclaimer').text} />
                    )}
                  </Form.Check.Label>
                </Form.Check>
              </Form.Group>
            </Col>
          </Row>
          <Row className="gx-3 gy-3 mt-1">
            <Col xs={12}>
              <Button
                className="justify-content-center mt-3"
                type="submit"
                size="sm"
                style={{ padding: '.5rem 3rem' }}
                disabled={submitting}
              >
                {submitting ? 'Please wait...' : getFieldDataByName('actions').submitLabel}
              </Button>
            </Col>
          </Row>
        </Form>
      </Row>
    </Container>
  );
};

export default FreeSampleForm;
